import { ActionTypeManageFCupEnum } from './friendsCup';

import { GDTApiErrors } from '@/types';
import { ResponseBase } from '@/types/commons';
import { CheckNameResponse } from '@/types/friendCup';
import {
  InviteUserByIdResponse,
  ResponseInvitarAmigo,
  SearchDtsResponse,
  TournamentInviteFormData,
} from '@/types/invites';
import {
  RequestManagePostulantML,
  ResponseBasicDataMinileague,
  RequestMiniliguesSearch,
  ResponseBuscarML,
  ResponseInitDesenlaceMl,
  ResponseInitMiniligas,
  ResponseListParticipatingMinileague,
  ResponseListParticipatingML,
  ResponseListPendingML,
  ResponseListPreviousMl,
  ResponseManagePostulantMl,
  ResponsePostulationMl,
  ResponseSugerirMl,
  ResponseVerEquiposML,
  ResponseVerML,
  RequestSaveChatMessages,
  ResponseSaveChatMessages,
  ResponseGetChatMessages,
  RequestGetChatMessages,
  ResponseCreateML,
  ResponseFinalWinnersMl,
  ResponseFinalWinnersMlFallback,
  ResponseUploadFotoML,
} from '@/types/minileagues';
import {
  ResponseReactivarMinileague,
  ResponseSuggestionAutocomplete,
} from '@/types/tda';
import { axiosErrorHandler, TEXTS, URLs } from '@/utils';
import axiosGdt, { axiosGdtWithoutCredentials } from '@/utils/axiosGdt';

export enum MiniLeagueErrorCode {
  MINI_LIGAS_FINALIZADO_ACEPT = 2611,
  EXCLUIDO_POR_SER_EXTRANJERO = 3507,
  MINI_LIGAS_COMPLETA = 2613,
  MINI_LIGAS_USUARIO_PARTICIPANDO = 2606,
  MINI_LIGAS_USUARIO_PENDIENTE_ACEPTAR_DUENIO = 2607,
  MINI_LIGAS_USUARIO_PENDIENTE_ACEPTAR_INV = 2623,
  ML_YA_INVITADO = 2622,
  ML_SIN_EQUIPO = 2623,
}

export const getInitMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.INIT}`;
  const response = await axiosGdt.get<ResponseInitMiniligas>(url);

  return response.data;
}, TEXTS.defaultError);

export const getViewMl = axiosErrorHandler(async (idMiniLiga: number) => {
  const url = `${URLs.MINILIGAS.VER_MINILIGA}&idMiniLiga=${idMiniLiga}`;
  const response = await axiosGdt.get<ResponseVerML>(url);

  return response.data;
}, TEXTS.defaultError);

export const getViewRivalMl = axiosErrorHandler(
  async (idMiniLiga: number, matchDay: number) => {
    const url = `${URLs.MINILIGAS.VER_RIVAL}&idMiniLiga=${idMiniLiga}&ordenFechaMiniligaActual=${matchDay}`;
    const response = await axiosGdt.get<ResponseVerEquiposML>(url);

    return response.data;
  },
  TEXTS.defaultError
);

export const getInitDesenlaceMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.INIT_DESENLACE}`;
  const response = await axiosGdt.get<ResponseInitDesenlaceMl>(url);

  return response.data;
}, TEXTS.defaultError);

export const getFinalWinnersMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.GANADORES}`;
  const response = await axiosGdt.get<ResponseFinalWinnersMl>(url);

  return response.data;
}, TEXTS.defaultError);
export const getFinalWinnersMlFallback = axiosErrorHandler(
  async (idDate: number) => {
    const url = `${URLs.MINILIGAS.GANADORES_EMERGENCIA}${idDate}.json`;
    const response =
      await axiosGdtWithoutCredentials.get<ResponseFinalWinnersMlFallback>(url);

    return response.data;
  },
  TEXTS.defaultError
);
// trae la lista de "Te invitaron a participar"
// ademas trae la lista de "Esperando aceptación"
export const getListPendingMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.LISTA_INVIT_PENDIENTES}`;
  const response = await axiosGdt.get<ResponseListPendingML>(url);

  return response.data;
}, TEXTS.defaultError);

export const getListParticipatingMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.LISTA_PARTICIPANDO}`;
  const response = await axiosGdt.get<ResponseListParticipatingML>(url);

  return response.data;
}, TEXTS.defaultError);

export const getListPreviousMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.LISTA_EDICION_PASADA}`;
  const response = await axiosGdt.get<ResponseListPreviousMl>(url);

  return response.data;
}, TEXTS.defaultError);

export const getListSuggestedUsersMl = axiosErrorHandler(async () => {
  const url = `${URLs.MINILIGAS.LISTA_SUGERIDOS}`;
  const response = await axiosGdt.get<ResponseSugerirMl>(url);

  return response.data;
}, TEXTS.defaultError);

export const requestPostulationMl = axiosErrorHandler(
  async (idMiniLiga: number) => {
    const url = `${URLs.MINILIGAS.ENVIAR_SOLICITUD_MINILIGA}`;
    const response = await axiosGdt.post<ResponsePostulationMl>(url, null, {
      params: { idMiniLiga },
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const getFullInitMl = axiosErrorHandler(async () => {
  const promise1: Promise<Promise<GDTApiErrors | ResponseInitMiniligas>> =
    getInitMl();
  const promise2: Promise<Promise<GDTApiErrors | ResponseListPendingML>> =
    getListPendingMl();
  const promise3: Promise<Promise<GDTApiErrors | ResponseListParticipatingML>> =
    getListParticipatingMl();
  const promise4: Promise<Promise<GDTApiErrors | ResponseListPreviousMl>> =
    getListPreviousMl();
  const promise5: Promise<Promise<GDTApiErrors | ResponseSugerirMl>> =
    getListSuggestedUsersMl();

  const [
    init,
    listPendingMl,
    listParticipatingMl,
    listPreviousMl,
    listSuggestedUsersMl,
  ] = await Promise.all([promise1, promise2, promise3, promise4, promise5]);

  return {
    init,
    listPendingMl,
    listParticipatingMl,
    listPreviousMl,
    listSuggestedUsersMl,
  };
}, 'Error al obtener datos');

export const requestPostulantManagementMl = axiosErrorHandler(
  async (data: RequestManagePostulantML) => {
    const { TRATAR_POSTULANTE } = URLs.MINILIGAS;
    const { tipoAccion, idMiniLiga, idUsuario, idParticipante } = data;

    const params = new URLSearchParams({
      tipoAccion: tipoAccion.toString(),
      idMiniLiga: idMiniLiga.toString(),
      ...(tipoAccion <= ActionTypeManageFCupEnum.ACC_RECHAZAR_POSTULACION &&
        idParticipante && {
          idParticipante: idParticipante.toString(),
        }),
      ...(tipoAccion ===
        ActionTypeManageFCupEnum.ACC_ACEPTAR_POSTULACION_DSD_INV &&
        idUsuario && {
          idUsuario: idUsuario?.toString(),
        }),
    });

    const response = await axiosGdt.post<ResponseManagePostulantMl>(
      `${TRATAR_POSTULANTE}&${params}`
    );

    return response.data;
  },
  TEXTS.defaultError
);

export const acceptMlInvitation = async (idMl: number) => {
  // Usamos la URL correcta desde URLS.COPA_AMIGOS.TRATAR_POSTULANTE
  const url = URLs.MINILIGAS.TRATAR_POSTULANTE;
  const params = new URLSearchParams({
    tipoAccion: '3', // ACC_ACEPTAR_INVITACION
    idMiniLiga: idMl.toString(),
  });

  // Realizar la solicitud a la API
  const response = await axiosGdt.post<ResponseBase>(
    `${url}&${params.toString()}`
  );
  return response.data;
};
export const createMiniLeague = axiosErrorHandler(async (name: string) => {
  const url = `${URLs.MINILIGAS.CREAR}${name}`;
  const response = await axiosGdt.post<ResponseCreateML>(
    url,
    {},
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
});

export const requestCheckNameAvailability = axiosErrorHandler(
  async (groupName: string) => {
    const url = `${URLs.MINILIGAS.VALIDAR_NOMBRE}`;
    const params = `nombre=${groupName}`;

    const response = await axiosGdt.get<CheckNameResponse | GDTApiErrors>(
      `${url}?${params}`
    );

    return response.data;
  },
  TEXTS.defaultError
);

export const listParticipantsMinileague = axiosErrorHandler(
  async (idMiniLiga: number, showInvited = false) => {
    const url = `${URLs.MINILIGAS.LISTAR_PARTICIPANTES}`;
    const response = await axiosGdt.get<ResponseListParticipatingMinileague>(
      `${url}&idMiniLiga=${idMiniLiga}${showInvited ? '&idTipoListado=2' : ''}`
    );

    return response.data;
  }
);

export const pushOutMinileagueMember = axiosErrorHandler(
  async (idMiniLiga: number, idUsuario: number) => {
    const url = `${URLs.MINILIGAS.EXPULSAR_MIEMBRO}`;
    const response = await axiosGdt.post(url, null, {
      params: {
        idMiniLiga,
        idUsuario,
      },
    });

    return response.data;
  }
);

export const inviteMinileagueMember = axiosErrorHandler(
  async (idMiniLiga: number, idUsuarioDT: number) => {
    const url = `${URLs.MINILIGAS.INVITAR_DT_POR_ID}`;
    const response = await axiosGdt.post(url, null, {
      params: {
        idMiniLiga,
        idUsuarioDT,
      },
    });

    return response.data;
  }
);

/**

tipoAccion:
 
1, ACEPTAR_POSTULACION;

2, RECHAZAR_POSTULACION;

3, ACEPTAR_INVITACION;

4, RECHAZAR_INVITACION
 */

export const manageMLRequest = axiosErrorHandler(
  async (idMiniLiga: number, idUsuario: number, tipoAccion: 1 | 2) => {
    const url = `${URLs.MINILIGAS.TRATAR_POSTULANTE}`;
    const response = await axiosGdt.post(url, null, {
      params: {
        idMiniLiga,
        idUsuario,
        tipoAccion,
      },
    });

    return response.data;
  }
);

export const basicDataMinileague = async (id: number) => {
  const url = `${URLs.MINILIGAS.DATOS_BASICOS}`;
  const response = await axiosGdt.get<ResponseBasicDataMinileague>(url, {
    params: {
      id,
    },
  });

  return response.data;
};

export const requestSearchMinileagues = axiosErrorHandler(
  async (data: RequestMiniliguesSearch) => {
    const url = `${URLs.MINILIGAS.BUSCAR_MINILIGA}`;
    const params = new URLSearchParams({
      nombreMiniLiga: String(data.nombreMiniLiga),
      nombreDueno: String(data.nombreDueno),
      apellidoDueno: String(data.apellidoDueno),
      paginaActual: String(data.paginaActual),
      cantPorPagina: String(data.cantPorPagina),
    });
    const response = await axiosGdt.get<ResponseBuscarML>(url, {
      params,
    });

    return response.data;
  },
  TEXTS.defaultError
);

export const searchDtsByName = axiosErrorHandler(
  async (search: string, idML: number) => {
    const params = new URLSearchParams({
      search,
      idML: idML.toString(),
    });
    const url = `${URLs.MINILIGAS.BUSCAR_DTS}`;
    const response = await axiosGdt.get<SearchDtsResponse | GDTApiErrors>(url, {
      params,
    });
    return response.data;
  }
);

export const inviteUserById = axiosErrorHandler(
  async (idMiniLiga: number, idUsuarioDT: number) => {
    const params = new URLSearchParams({
      idMiniLiga: idMiniLiga.toString(),
      idUsuarioDT: idUsuarioDT.toString(),
    });
    const url = `${URLs.MINILIGAS.INVITAR_POR_ID}`;
    const response = await axiosGdt.get<InviteUserByIdResponse | GDTApiErrors>(
      url,
      { params }
    );
    return response.data;
  }
);

export const inviteByEmail = axiosErrorHandler(
  async (data: TournamentInviteFormData) => {
    const params = new URLSearchParams({
      idMiniLiga: data.idMiniLiga.toString(),
      nombre: data.names.join('|'),
      mail: data.emails.join('|'),
    });
    const url = `${URLs.MINILIGAS.INVITAR_EMAIL}`;
    const response = await axiosGdt.get<ResponseInvitarAmigo | GDTApiErrors>(
      url,
      { params }
    );
    return response.data;
  }
);

// is a post with idMiniLiga and name
export const reactivateMiniLeague = axiosErrorHandler(
  async (idMiniLiga: number, name: string) => {
    const url = `${URLs.MINILIGAS.REACTIVAR}`;
    const response = await axiosGdt.post<ResponseReactivarMinileague>(
      url,
      null,
      {
        params: {
          idMiniLiga,
          nombre: name,
        },
      }
    );

    return response.data;
  },
  TEXTS.defaultError
);

// Pedido para autocompletado de busqueda de torneos existentes
export const requestMlNameAutocomplete = axiosErrorHandler(
  async (term: string) => {
    const url = `${
      URLs.MINILIGAS.AUTOCOMPLETE_NOMBRE_ML
    }&term=${encodeURIComponent(term)}`;
    const response = await axiosGdt.post<ResponseSuggestionAutocomplete>(url);
    return response.data;
  },
  TEXTS.defaultError
);

export const requestMlDTsAutocomplete = axiosErrorHandler(
  async (term: string) => {
    const params = new URLSearchParams({
      term: encodeURIComponent(term),
    });
    const response = await axiosGdt.post<ResponseSuggestionAutocomplete>(
      URLs.MINILIGAS.AUTOCOMPLETE_APELLIDO_MLDT,
      null,
      { params }
    );
    return response.data;
  },
  TEXTS.defaultError
);

export const requestAutocompleteSuggestions = axiosErrorHandler(
  async (nameMl: string, lastnameDt: string) => {
    const promise1: Promise<Promise<ResponseSuggestionAutocomplete>> =
      requestMlNameAutocomplete(nameMl);
    const promise2: Promise<Promise<ResponseSuggestionAutocomplete>> =
      requestMlDTsAutocomplete(lastnameDt);
    const [responseTourneyName, responseTourneyDts] = await Promise.all([
      promise1,
      promise2,
    ]);
    return {
      responseTourneyName,
      responseTourneyDts,
    };
  },
  TEXTS.defaultError
);

export const requestGetChatMessages = axiosErrorHandler(
  async ({
    idMiniLiga,
    cantPorPagina,
    paginaActual,
  }: RequestGetChatMessages) => {
    const url = `${URLs.MINILIGAS.OBTENER_MENSAJES_CHAT}`;
    const params = new URLSearchParams({
      idMiniLiga: String(idMiniLiga),
      cantPorPagina: String(cantPorPagina || 5),
      paginaActual: String(paginaActual || 1),
    });
    const response = await axiosGdt.get<ResponseGetChatMessages>(
      `${url}?${params}`
    );
    return response.data;
  },
  TEXTS.defaultError
);

export const requestPostChatMessages = axiosErrorHandler(
  async ({ idMiniLiga, mensaje }: RequestSaveChatMessages) => {
    const url = `${URLs.MINILIGAS.ENVIAR_MENSAJE_CHAT}`;
    const params = new URLSearchParams({
      idMiniLiga: String(idMiniLiga),
      mensaje,
      menciones: String(-1),
    });
    const response = await axiosGdt.post<ResponseSaveChatMessages>(
      `${url}?${params}`
    );
    return response.data;
  },
  TEXTS.defaultError
);

/**
 * Upload foto de perfil
 */
export const uploadProfilePic = axiosErrorHandler(
  async (blob: Blob, idTorneo: number) => {
    const url = `${URLs.MINILIGAS.SUBIR_FOTO_PERFIL}?idML=${idTorneo}`;
    const formData = new FormData();
    const fileName = `archivo.jpg`;
    const file = new File([blob], fileName);
    formData.append('archivo', file, fileName);

    const response = await axiosGdt.post<ResponseUploadFotoML>(url, formData, {
      headers: {
        'Content-Type': `multipart/form-data`,
      },
    });
    return response.data;
  },
  TEXTS.defaultError
);

/**
 * selecciona  foto de perfil predefinida
 */
export const setPredefinedProfilePic = axiosErrorHandler(
  async (idML: number, idPredefinida: number) => {
    const url = `${URLs.MINILIGAS.SELECCIONAR_FOTO_PERFIL_PREDEFINIDA}`;
    const response = await axiosGdt.post<ResponseUploadFotoML>(
      url,
      {
        idPredefinida,
        idML,
      },
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
      }
    );
    return response.data;
  },
  TEXTS.defaultError
);
